import React, { useMemo } from 'react';
import { Spin, Typography } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PrintPreviewChrome } from '@/components/print/PrintPreviewChrome';
import { ProductionApprovalFormPrint } from '@/components/manufacturing/ProductionApprovalFormPrint';
import * as prApi from '@/services/productionRequest.service';
import { useSystemSettings } from '@/hooks/useSettings';
import type { SystemSetting } from '@/services/settingsService';

const { Text } = Typography;

function getSetting(settings: SystemSetting[] | undefined, key: string): string {
  if (!settings?.length) return '';
  const s = settings.find((x) => x.setting_key === key);
  return (s?.setting_value ?? '').trim();
}

/**
 * Printable “Production approval form” layout (A4) for customer / PO waiver workflow.
 */
const ProductionRequestApprovalPrintPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: pr, isLoading, error } = useQuery({
    queryKey: ['production-request', id],
    queryFn: () => prApi.getProductionRequest(id!),
    enabled: Boolean(id),
  });
  const { data: settings } = useSystemSettings();

  const company = useMemo(() => {
    const list = settings?.filter((s) => s.category === 'company') ?? [];
    const name = getSetting(list, 'company_name') || 'Company';
    const legal = getSetting(list, 'company_legal_name') || getSetting(list, 'company_name') || name;
    const logo = getSetting(list, 'company_logo') || undefined;
    const docNo = getSetting(list, 'production_approval_form_document_no') || undefined;
    return { name, legalName: legal, logoUrl: logo, documentFormNo: docNo };
  }, [settings]);

  if (!id) {
    return (
      <div style={{ padding: 24 }}>
        <Text type="danger">Missing request id</Text>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !pr) {
    return (
      <div style={{ padding: 24 }}>
        <Text type="danger">Production request not found.</Text>
      </div>
    );
  }

  return (
    <PrintPreviewChrome title={`Approval form — ${pr.request_number}`} fileName={pr.request_number} onBack={() => navigate(`/mfg/production-requests/${id}`)}>
      <ProductionApprovalFormPrint
        pr={pr}
        companyLegalName={company.legalName}
        companyLogoUrl={company.logoUrl}
        documentFormNo={company.documentFormNo}
      />
    </PrintPreviewChrome>
  );
};

export default ProductionRequestApprovalPrintPage;
